import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { usePeopleSearch } from "@/contexts/PeopleSearchContext";
import { useOrgMembers } from "@/hooks/peopleSearch/useOrgMembers";

export function ProfileInfoPanel() {
	const { t } = useTranslation();
	const { draft, updateDraft } = usePeopleSearch();
	const { data: members, isLoading } = useOrgMembers();
	const profile = draft.profile;

	const toggleManager = (managerId: string, checked: boolean) => {
		const managerIds = checked
			? [...profile.managerIds, managerId]
			: profile.managerIds.filter(id => id !== managerId);
		updateDraft('profile', { ...profile, managerIds });
	};

	return (
		<div className="space-y-6">
			<div className="space-y-2">
				<Label htmlFor="people-search-name">{t("peopleSearch.profile.name", "Name")}</Label>
				<Input
					id="people-search-name"
					value={profile.name}
					onChange={e => updateDraft('profile', { ...profile, name: e.target.value })}
					placeholder={t("peopleSearch.profile.namePlaceholder", "What's their name?")}
				/>
			</div>

			<div className="space-y-2">
				<Label>{t("peopleSearch.profile.addedBy", "Added to database by")}</Label>
				{isLoading ? (
					<div className="space-y-2">
						{Array.from({ length: 3 }).map((_, i) => (
							<Skeleton key={i} className="h-6 w-full" />
						))}
					</div>
				) : (
					<div className="space-y-1 max-h-[200px] overflow-y-auto border rounded-sm p-2">
						{(members || []).map(member => (
							<label key={member.id} className="flex items-center gap-2 px-1 py-1 rounded-sm hover:bg-muted cursor-pointer">
								<Checkbox
									checked={profile.managerIds.includes(member.id)}
									onCheckedChange={checked => toggleManager(member.id, !!checked)}
								/>
								<span className="text-sm truncate">{member.name}</span>
							</label>
						))}
					</div>
				)}
			</div>

			<div className="space-y-2">
				<Label>{t("peopleSearch.profile.whenAdded", "When added to the database")}</Label>
				<div className="flex items-center gap-2">
					<Input
						type="date"
						value={profile.createdFrom || ''}
						onChange={e => updateDraft('profile', { ...profile, createdFrom: e.target.value || null })}
						aria-label={t("peopleSearch.profile.from", "From")}
					/>
					<span className="text-muted-foreground text-sm">→</span>
					<Input
						type="date"
						value={profile.createdTo || ''}
						onChange={e => updateDraft('profile', { ...profile, createdTo: e.target.value || null })}
						aria-label={t("peopleSearch.profile.to", "To")}
					/>
				</div>
			</div>
		</div>
	);
}
